import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Users, Search, RefreshCw, ChevronLeft, ChevronRight, Download, Eye, StickyNote, Trash2, CheckCircle, MessageCircle, X, Linkedin, Github, Twitter, Instagram, Globe } from 'lucide-react';
import { API_BASE, getAdminToken, formatDate, Modal } from './adminUtils';
import { apiService } from '../../services/api';

export const UsersTab = ({ onStatsRefresh }) => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [togglingUser, setTogglingUser] = useState(null);

  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProgress, setUserProgress] = useState(null);
  const [userNotes, setUserNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [progressLoading, setProgressLoading] = useState(false);

  // Profile side panel
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const [panelUser, setPanelUser] = useState(null);
  const [panelData, setPanelData] = useState(null);
  const [loadingPanel, setLoadingPanel] = useState(false);

  // Delete-user state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetUser, setDeleteTargetUser] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = useCallback(async () => {
    const token = getAdminToken(); if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '20', ...(search && { search }), ...(statusFilter !== 'all' && { status_filter: statusFilter }) });
      const res = await fetch(`${API_BASE}/api/admin/users?${params}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setUsers(d.users); setTotalPages(d.pages); setTotal(d.total); }
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const toggleUserStatus = async (userId, currentStatus) => {
    const token = getAdminToken(); if (!token) return;
    setTogglingUser(userId);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/toggle-status`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ user_id: userId, is_active: !currentStatus })
      });
      if (res.ok) { toast.success((await res.json()).message); setUsers(users.map(u => u.id === userId ? { ...u, is_active: !currentStatus } : u)); onStatsRefresh?.(); }
    } catch { toast.error('Failed'); }
    finally { setTogglingUser(null); }
  };

  const exportCSV = async () => {
    const token = getAdminToken(); if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/export/users`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        const blob = new Blob([d.csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = d.filename; a.click();
        toast.success('Downloaded');
      }
    } catch { toast.error('Export failed'); }
  };

  const openProgressModal = async (user) => {
    setSelectedUser(user); setShowProgressModal(true); setProgressLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${user.id}/progress`, { headers: { 'Authorization': `Bearer ${getAdminToken()}` } });
      if (res.ok) setUserProgress(await res.json());
    } catch { toast.error('Failed to load progress'); }
    finally { setProgressLoading(false); }
  };

  const openNotesModal = async (user) => {
    setSelectedUser(user); setShowNotesModal(true); setNewNote('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${user.id}/notes`, { headers: { 'Authorization': `Bearer ${getAdminToken()}` } });
      if (res.ok) setUserNotes((await res.json()).notes);
    } catch { toast.error('Failed to load notes'); }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedUser) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${selectedUser.id}/notes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAdminToken()}` },
        body: JSON.stringify({ note: newNote })
      });
      if (res.ok) { toast.success('Added'); setNewNote(''); openNotesModal(selectedUser); }
    } catch { toast.error('Failed'); }
  };

  const deleteNote = async (noteId) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${selectedUser.id}/notes/${noteId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${getAdminToken()}` }
      });
      if (res.ok) { toast.success('Deleted'); openNotesModal(selectedUser); }
    } catch { toast.error('Failed'); }
  };

  const adminDownloadCV = async (cvUrl, userName) => {
    const token = getAdminToken();
    const response = await fetch(`${API_BASE}/api/admin/users/${panelUser.id}/cv/download`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) return;
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${userName || 'resume'}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const openProfilePanel = async (user) => {
    setPanelUser(user);
    setPanelData(null);
    setShowProfilePanel(true);
    setLoadingPanel(true);
    try {
      const data = await apiService.adminGetUserProfile(user.id, getAdminToken());
      setPanelData(data);
    } catch {
      toast.error('Failed to load user profile');
    } finally {
      setLoadingPanel(false);
    }
  };

  const openDeleteModal = (user) => {
    setDeleteTargetUser(user);
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!deleteTargetUser) return;
    if (deleteConfirmText.trim().toLowerCase() !== (deleteTargetUser.email || '').toLowerCase()) {
      toast.error('Email does not match — type the exact email to confirm');
      return;
    }
    const token = getAdminToken(); if (!token) return;
    setDeleting(true);
    try {
      const url = `${API_BASE}/api/admin/users/${deleteTargetUser.id}?confirm_email=${encodeURIComponent(deleteTargetUser.email)}`;
      const res = await fetch(url, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        const total = Object.values(d.deleted_from || {}).reduce((a, b) => a + b, 0);
        toast.success(`Deleted ${deleteTargetUser.email} (${total} record${total === 1 ? '' : 's'} cleaned)`);
        setShowDeleteModal(false);
        setDeleteTargetUser(null);
        setDeleteConfirmText('');
        fetchUsers();
        onStatsRefresh?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Delete failed');
      }
    } catch {
      toast.error('Delete request failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Card className="bg-white/5 border-white/10 backdrop-blur">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />User Management
              <Badge className="bg-blue-500/20 text-blue-300 ml-2">{total}</Badge>
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Name or email…" value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-white/10 border-white/20 text-white w-44" />
              </div>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-white/10 border border-white/20 text-white rounded-md px-3 py-2 text-sm">
                <option value="all" className="bg-slate-800">All</option>
                <option value="active" className="bg-slate-800">Active</option>
                <option value="disabled" className="bg-slate-800">Disabled</option>
              </select>
              <Button variant="outline" size="sm" onClick={exportCSV} className="border-white/20 text-white">
                <Download className="w-4 h-4 mr-1" />CSV
              </Button>
              <Button variant="outline" size="icon" onClick={() => { fetchUsers(); onStatsRefresh?.(); }} className="border-white/20 text-white">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 text-amber-500 animate-spin" /></div> : (
            <>
              <div className="rounded-xl border border-white/10 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-slate-300">Email</TableHead>
                      <TableHead className="text-slate-300">Name</TableHead>
                      <TableHead className="text-slate-300">Mobile</TableHead>
                      <TableHead className="text-slate-300">Joined</TableHead>
                      <TableHead className="text-slate-300">Status</TableHead>
                      <TableHead className="text-slate-300 text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No users</TableCell></TableRow> :
                      users.map(u => (
                        <TableRow key={u.id} className="border-white/10 hover:bg-white/5">
                          <TableCell className="text-white font-medium">{u.email}</TableCell>
                          <TableCell className="text-slate-300">{u.full_name || '-'}</TableCell>
                          <TableCell className="text-slate-300">{u.mobile || '-'}</TableCell>
                          <TableCell className="text-slate-400 text-sm">{formatDate(u.created_at)}</TableCell>
                          <TableCell><Badge className={u.is_active ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}>{u.is_active ? 'Active' : 'Disabled'}</Badge></TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openProfilePanel(u)} className="text-blue-400 hover:bg-blue-500/20" title="View profile">
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => openNotesModal(u)} className="text-amber-400 hover:bg-amber-500/20" title="Notes">
                                <StickyNote className="w-4 h-4" />
                              </Button>
                              {u.mobile && (
                                <a href={`https://wa.me/${u.mobile.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" title="WhatsApp">
                                  <Button variant="ghost" size="icon" className="text-green-400 hover:bg-green-500/20">
                                    <MessageCircle className="w-4 h-4" />
                                  </Button>
                                </a>
                              )}
                              <Switch checked={u.is_active} onCheckedChange={() => toggleUserStatus(u.id, u.is_active)} disabled={togglingUser === u.id} />
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openDeleteModal(u)}
                                className="text-red-400 hover:bg-red-500/20"
                                title="Delete user permanently"
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
                  <p className="text-sm text-slate-400">Page {page} of {totalPages} <span className="text-slate-500">· {total} total</span></p>
                  <div className="flex flex-wrap items-center gap-1">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="border-white/20 text-white"><ChevronLeft className="w-4 h-4" /></Button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                      <Button
                        key={n}
                        variant={n === page ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPage(n)}
                        className={n === page ? 'bg-purple-500 hover:bg-purple-600 text-white min-w-[2.25rem]' : 'border-white/20 text-white min-w-[2.25rem]'}
                      >
                        {n}
                      </Button>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="border-white/20 text-white"><ChevronRight className="w-4 h-4" /></Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Profile Side Panel ───────────────────────────────────────── */}
      {showProfilePanel && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/60 z-40" onClick={() => setShowProfilePanel(false)} />
          {/* Panel */}
          <div className="fixed right-0 top-0 h-full w-[420px] max-w-full bg-slate-900 border-l border-white/10 z-50 flex flex-col shadow-2xl">
            {/* Panel header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
              <h2 className="text-base font-semibold text-white">User Profile</h2>
              <button onClick={() => setShowProfilePanel(false)} className="text-slate-400 hover:text-white p-1 rounded transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Panel body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {loadingPanel ? (
                <div className="flex justify-center py-16"><RefreshCw className="w-8 h-8 text-amber-500 animate-spin" /></div>
              ) : panelData ? (
                <>
                  {/* ── Section 1: Identity header ──────────────────── */}
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                      <span className="text-xl font-bold text-white">
                        {(panelData.first_name?.[0] || panelData.email?.[0] || '?').toUpperCase()}
                        {(panelData.last_name?.[0] || '').toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-white font-semibold text-base leading-tight">{panelData.full_name || '—'}</p>
                      <p className="text-slate-400 text-sm mt-0.5 break-all">{panelData.email}</p>
                      {panelData.mobile_number && (
                        <p className="text-slate-500 text-xs mt-0.5">📞 {panelData.mobile_number.startsWith('+') ? panelData.mobile_number : `+${panelData.mobile_number}`}</p>
                      )}
                      {panelData.created_at && (
                        <p className="text-slate-600 text-xs mt-0.5">Member since {new Date(panelData.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long' })}</p>
                      )}
                    </div>
                  </div>

                  {/* ── Section 2: Stats ────────────────────────────── */}
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'Labs Attempted', value: panelData.stats.total_labs },
                      { label: 'Labs Completed', value: panelData.stats.completed_labs },
                      { label: 'Score', value: panelData.stats.total_score },
                      { label: 'Courses Purchased', value: panelData.stats.courses_purchased_count },
                    ].map((s) => (
                      <div key={s.label} className="bg-white/5 rounded-xl p-3 text-center border border-white/5">
                        <p className="text-xl font-bold text-white">{s.value}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* ── Section 3: Professional (skip if all empty) ──── */}
                  {(panelData.city || panelData.bio || panelData.current_title || panelData.company) && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Professional</p>
                      <div className="bg-white/5 rounded-xl p-4 space-y-2 border border-white/5">
                        {panelData.current_title && (
                          <p className="text-sm text-white font-medium">{panelData.current_title}{panelData.company ? <span className="text-slate-400 font-normal"> · {panelData.company}</span> : ''}</p>
                        )}
                        {!panelData.current_title && panelData.company && (
                          <p className="text-sm text-slate-300">{panelData.company}</p>
                        )}
                        {panelData.city && <p className="text-sm text-slate-400">📍 {panelData.city}</p>}
                        {panelData.bio && <p className="text-xs text-slate-400 leading-relaxed">{panelData.bio}</p>}
                      </div>
                    </div>
                  )}

                  {/* ── Section 4: Social links (only filled) ────────── */}
                  {(panelData.social_linkedin || panelData.social_github || panelData.social_twitter || panelData.social_instagram || panelData.social_website) && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Social Links</p>
                      <div className="flex flex-wrap gap-2">
                        {panelData.social_linkedin && (
                          <a href={panelData.social_linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-xs px-3 py-1.5 rounded-lg transition-colors">
                            <Linkedin className="w-3.5 h-3.5" /> LinkedIn
                          </a>
                        )}
                        {panelData.social_github && (
                          <a href={panelData.social_github} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 text-slate-300 text-xs px-3 py-1.5 rounded-lg transition-colors">
                            <Github className="w-3.5 h-3.5" /> GitHub
                          </a>
                        )}
                        {panelData.social_twitter && (
                          <a href={panelData.social_twitter} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 text-xs px-3 py-1.5 rounded-lg transition-colors">
                            <Twitter className="w-3.5 h-3.5" /> Twitter
                          </a>
                        )}
                        {panelData.social_instagram && (
                          <a href={panelData.social_instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 bg-pink-500/10 hover:bg-pink-500/20 text-pink-400 text-xs px-3 py-1.5 rounded-lg transition-colors">
                            <Instagram className="w-3.5 h-3.5" /> Instagram
                          </a>
                        )}
                        {panelData.social_website && (
                          <a href={panelData.social_website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs px-3 py-1.5 rounded-lg transition-colors">
                            <Globe className="w-3.5 h-3.5" /> Website
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ── Section 5: Certificates ─────────────────────── */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Certificates</p>
                    {panelData.certificates.length === 0 ? (
                      <p className="text-sm text-slate-500 bg-white/5 rounded-xl p-4 text-center border border-white/5">No certificates added</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {panelData.certificates.map((cert) => (
                          <span
                            key={cert.id}
                            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full text-white"
                            style={{ backgroundColor: `${cert.color}33`, border: `1px solid ${cert.color}55`, color: cert.color }}
                          >
                            {cert.catalog_name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* ── Section 6: CV ───────────────────────────────── */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">CV / Resume</p>
                    {panelData.cv_url ? (
                      <button
                        onClick={() => adminDownloadCV(panelData.cv_url, panelData.full_name || 'resume')}
                        className="w-full flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-3 text-sm text-emerald-400 transition-colors"
                      >
                        📄 <span>Download CV</span>
                      </button>
                    ) : (
                      <p className="text-sm text-slate-500 bg-white/5 rounded-xl p-3 text-center border border-white/5">No CV uploaded</p>
                    )}
                  </div>

                  {/* ── Section 7: Job Settings ─────────────────────── */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Job Settings</p>
                    <div className="bg-white/5 rounded-xl p-4 border border-white/5 flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-300">Job Notifications</p>
                        <p className="text-xs text-slate-500 mt-0.5 capitalize">Preference: {panelData.job_preference || 'any'}</p>
                      </div>
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${panelData.job_notifications ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-500'}`}>
                        {panelData.job_notifications ? 'On' : 'Off'}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-slate-400 text-center py-8">No data available</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Progress Modal */}
      <Modal show={showProgressModal} onClose={() => setShowProgressModal(false)} title={`Progress: ${selectedUser?.email || ''}`}>
        {progressLoading ? <div className="flex justify-center py-8"><RefreshCw className="w-8 h-8 text-amber-500 animate-spin" /></div> : userProgress ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/5 rounded-lg p-3"><p className="text-xs text-slate-400">Email</p><p className="text-white">{userProgress.user.email}</p></div>
              <div className="bg-white/5 rounded-lg p-3"><p className="text-xs text-slate-400">Name</p><p className="text-white">{userProgress.user.full_name || '-'}</p></div>
            </div>
            <div><h3 className="text-sm font-semibold text-white mb-2">Course Progress</h3>
              {userProgress.enrollments.length === 0 ? <p className="text-slate-400 text-sm">No progress data</p> : (
                <div className="space-y-3">{userProgress.enrollments.map((e, i) => (
                  <div key={i} className="bg-white/5 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-medium">{e.course_id}</p>
                      <div className="text-right">
                        <p className="text-emerald-400 font-bold">Score: {e.total_score || 0}</p>
                        <p className="text-xs text-slate-400">{e.total_labs_attempted || 0} labs attempted</p>
                      </div>
                    </div>
                    {e.enrolled_at && <p className="text-xs text-slate-500 mb-2">Enrolled: {formatDate(e.enrolled_at)}</p>}
                    {e.labs && e.labs.length > 0 && (
                      <div className="space-y-1 mt-2 border-t border-white/5 pt-2">
                        {e.labs.map((lab, j) => (
                          <div key={j} className="flex items-center justify-between text-sm">
                            <span className="text-slate-300 font-mono text-xs">{lab.lab_id}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-slate-400">{lab.completed_tasks} tasks</span>
                              <span className="text-amber-400">+{lab.score} pts</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}</div>
              )}
            </div>
            <div><h3 className="text-sm font-semibold text-white mb-2">Recent Sessions</h3>
              {userProgress.recent_sessions.length === 0 ? <p className="text-slate-400 text-sm">No sessions</p> : (
                <div className="space-y-2">{userProgress.recent_sessions.slice(0, 5).map((s, i) => (
                  <div key={i} className="bg-white/5 rounded-lg p-2 flex items-center justify-between text-sm">
                    <span className="text-white">{s.course} - {s.lab_id}</span>
                    <span className="text-slate-400">{formatDate(s.created_at)}</span>
                  </div>
                ))}</div>
              )}
            </div>
          </div>
        ) : <p className="text-slate-400">No data</p>}
      </Modal>

      {/* Notes Modal */}
      <Modal show={showNotesModal} onClose={() => setShowNotesModal(false)} title={`Notes: ${selectedUser?.email || ''}`}>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input placeholder="Add a note..." value={newNote} onChange={e => setNewNote(e.target.value)} className="bg-white/10 border-white/20 text-white flex-1" />
            <Button onClick={addNote} disabled={!newNote.trim()} className="bg-amber-500 hover:bg-amber-600 text-white">Add</Button>
          </div>
          {userNotes.length === 0 ? <p className="text-slate-400 text-center py-4">No notes</p> : (
            <div className="space-y-2">{userNotes.map(n => (
              <div key={n.id} className="bg-white/5 rounded-lg p-3 flex items-start justify-between gap-2">
                <div><p className="text-white">{n.note}</p><p className="text-xs text-slate-400 mt-1">{formatDate(n.created_at)}</p></div>
                <button onClick={() => deleteNote(n.id)} className="p-1 hover:bg-red-500/20 rounded text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}</div>
          )}
        </div>
      </Modal>

      {/* Delete User Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onClose={() => { if (!deleting) { setShowDeleteModal(false); setDeleteTargetUser(null); setDeleteConfirmText(''); } }}
        title="Delete user permanently"
      >
        {deleteTargetUser && (
          <div className="space-y-4" data-testid="delete-user-modal">
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200 space-y-1">
              <p className="font-semibold flex items-center gap-2">
                <Trash2 className="w-4 h-4" /> This action cannot be undone.
              </p>
              <p>
                The user, all their enrollments, payments, progress, notifications,
                lab attempts and notes will be hard-deleted from the database.
              </p>
            </div>

            <div className="bg-white/5 rounded-lg p-3 space-y-1 text-sm">
              <div className="flex justify-between gap-2">
                <span className="text-slate-400">Email</span>
                <span className="text-white font-medium break-all">{deleteTargetUser.email}</span>
              </div>
              {deleteTargetUser.full_name && (
                <div className="flex justify-between gap-2">
                  <span className="text-slate-400">Name</span>
                  <span className="text-white">{deleteTargetUser.full_name}</span>
                </div>
              )}
              {deleteTargetUser.mobile && (
                <div className="flex justify-between gap-2">
                  <span className="text-slate-400">Mobile</span>
                  <span className="text-white">{deleteTargetUser.mobile}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-300 block">
                Type <span className="font-mono text-amber-300">{deleteTargetUser.email}</span> to confirm:
              </label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Re-type the email exactly"
                className="bg-white/10 border-white/20 text-white"
                disabled={deleting}
                data-testid="delete-user-confirm-input"
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => { setShowDeleteModal(false); setDeleteTargetUser(null); setDeleteConfirmText(''); }}
                disabled={deleting}
                className="border-white/20 text-white"
                data-testid="delete-user-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteUser}
                disabled={deleting || deleteConfirmText.trim().toLowerCase() !== (deleteTargetUser.email || '').toLowerCase()}
                className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                data-testid="delete-user-confirm"
              >
                {deleting ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                {deleting ? 'Deleting…' : 'Delete user permanently'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};
